import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  ContentCopy,
  Person,
  Refresh,
  QrCode,
} from '@mui/icons-material';
import { btApi, BTKey, BTTeacher, ChurchManager } from '../../services/api/btApi';

const BTKeyManagePage: React.FC = () => {
  const { churchManagerId } = useParams<{ churchManagerId: string }>();
  const navigate = useNavigate();

  const [keys, setKeys] = useState<BTKey[]>([]);
  const [churchInfo, setChurchInfo] = useState<ChurchManager | null>(null);
  const [teachers, setTeachers] = useState<BTTeacher[]>([]);
  const [assignDialog, setAssignDialog] = useState(false);
  const [selectedKey, setSelectedKey] = useState<BTKey | null>(null);
  const [teacherFormData, setTeacherFormData] = useState({
    teacherName: '',
    teacherNameEn: '',
    teacherPhone: '',
    teacherEmail: '',
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    if (churchManagerId) {
      fetchData();
    }
  }, [churchManagerId]);

  const fetchData = async () => {
    if (!churchManagerId) return;

    setLoading(true);
    try {
      // 교회 정보 조회
      const churchResponse = await btApi.getChurchManagerById(churchManagerId);
      setChurchInfo(churchResponse);

      // 키 목록 조회
      const keysResponse = await btApi.getKeysByChurchManager(churchManagerId);
      setKeys(keysResponse.data || []);

      // 교사 목록 조회
      const teachersResponse = await btApi.getTeachersByChurchManager(churchManagerId);
      setTeachers(teachersResponse.data || []);

    } catch (error: any) {
      console.error('데이터 조회 오류:', error);
      setSnackbar({
        open: true,
        message: '데이터를 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignKey = async () => {
    if (!selectedKey || !churchManagerId) return;

    try {
      // 1. BTTeacher 문서 직접 생성
      const newTeacher = await btApi.createBTTeacher({
        teacherName: teacherFormData.teacherName,
        teacherNameEn: teacherFormData.teacherNameEn,
        teacherPhone: teacherFormData.teacherPhone,
        teacherEmail: teacherFormData.teacherEmail,
        churchManagerId: churchManagerId,
        status: 'approved',
      });

      if (!newTeacher || !newTeacher._id) {
        throw new Error('교사 저장에 실패했습니다.');
      }

      // 2. 키 할당
      await btApi.assignKeyToTeacher(selectedKey.keyCode, newTeacher._id);

      setSnackbar({
        open: true,
        message: '키가 교사에게 할당되었습니다.',
        severity: 'success',
      });

      // 데이터 새로고침
      fetchData();

      setAssignDialog(false);
      setSelectedKey(null);
      setTeacherFormData({
        teacherName: '',
        teacherNameEn: '',
        teacherPhone: '',
        teacherEmail: '',
      });
    } catch (error: any) {
      console.error('키 할당 오류:', error);
      setSnackbar({
        open: true,
        message: error.message || '키 할당 중 오류가 발생했습니다.',
        severity: 'error',
      });
    }
  };

  const openAssignDialog = (key: BTKey) => {
    setSelectedKey(key);
    setAssignDialog(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({
        open: true,
        message: '클립보드에 복사되었습니다.',
        severity: 'success',
      });
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'primary';
      case 'assigned': return 'warning';
      case 'used': return 'success';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '사용 가능';
      case 'assigned': return '할당됨';
      case 'used': return '사용됨';
      case 'expired': return '만료됨';
      default: return status;
    }
  };

  const getTeacherInfo = (key: BTKey) => {
    // 키에 교사 정보가 직접 포함된 경우
    if (key.assignedTeacher) {
      return key.assignedTeacher;
    }
    // assignedTeacherId가 populate된 경우 (객체로 반환됨)
    if (key.assignedTeacherId && typeof key.assignedTeacherId === 'object') {
      return key.assignedTeacherId as unknown as BTTeacher;
    }
    // assignedTeacherId가 문자열인 경우 teachers 배열에서 찾기
    if (key.assignedTeacherId && typeof key.assignedTeacherId === 'string') {
      return teachers.find(t => t._id === key.assignedTeacherId);
    }
    return null;
  };

  const availableKeys = keys.filter(k => k.status === 'available');
  const assignedKeys = keys.filter(k => k.status === 'assigned');
  const usedKeys = keys.filter(k => k.status === 'used');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/bt')}
          variant="outlined"
        >
          BT 관리로
        </Button>
        <Typography variant="h4" component="h1">
          키 관리 - {churchInfo?.churchName}
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={fetchData}
          variant="outlined"
        >
          새로고침
        </Button>
      </Box>

      {/* 교회 정보 카드 */}
      {churchInfo && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom>교회 정보</Typography>
                <Typography variant="body2">교회명: {churchInfo.churchName}</Typography>
                <Typography variant="body2">담당자: {churchInfo.managerName}</Typography>
                <Typography variant="body2">연락처: {churchInfo.managerPhone}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom>키 현황</Typography>
                <Typography variant="body2">총 발급: {keys.length}개</Typography>
                <Typography variant="body2">사용 가능: {availableKeys.length}개</Typography>
                <Typography variant="body2">할당됨: {assignedKeys.length}개</Typography>
                <Typography variant="body2">사용됨: {usedKeys.length}개</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>사용 가능</Typography>
              <Typography variant="h4">{availableKeys.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>할당됨</Typography>
              <Typography variant="h4">{assignedKeys.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>사용됨</Typography>
              <Typography variant="h4">{usedKeys.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>사용률</Typography>
              <Typography variant="h4">
                {keys.length > 0 ? Math.round((usedKeys.length / keys.length) * 100) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 키 목록 테이블 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>키 코드</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>할당된 교사</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>할당일</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>QR 코드</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    데이터를 불러오는 중...
                  </TableCell>
                </TableRow>
              ) : keys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    생성된 키가 없습니다.
                  </TableCell>
                </TableRow>
              ) : keys.map((key) => (
                <TableRow key={key._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontWeight: 600,
                        }}
                      >
                        {key.keyCode}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(key.keyCode)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(key.status)}
                      color={getStatusColor(key.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const teacher = getTeacherInfo(key);
                      if (teacher) {
                        return (
                          <Box>
                            <Typography variant="body2">
                              {teacher.teacherName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {teacher.teacherPhone}
                            </Typography>
                          </Box>
                        );
                      }
                      return '-';
                    })()}
                  </TableCell>
                  <TableCell>
                    {key.assignedDate 
                      ? new Date(key.assignedDate).toLocaleDateString('ko-KR')
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    {key.qrCode ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QrCode color="success" />
                        <Typography variant="caption">생성됨</Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {key.status === 'available' && (
                      <Tooltip title="교사에게 할당">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openAssignDialog(key)}
                        >
                          <Person />
                        </IconButton>
                      </Tooltip>
                    )}
                    {key.qrCode && (
                      <Tooltip title="QR 코드 복사">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => copyToClipboard(key.qrCode!)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 교사 할당 다이얼로그 */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>교사에게 키 할당</DialogTitle>
        <DialogContent>
          {selectedKey && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                키 코드: <strong>{selectedKey.keyCode}</strong>
              </Alert>
              
              <TextField
                fullWidth
                label="교사 이름"
                value={teacherFormData.teacherName}
                onChange={(e) => setTeacherFormData({...teacherFormData, teacherName: e.target.value})}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="영문 이름"
                value={teacherFormData.teacherNameEn}
                onChange={(e) => setTeacherFormData({...teacherFormData, teacherNameEn: e.target.value.toUpperCase()})}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="전화번호"
                value={teacherFormData.teacherPhone}
                onChange={(e) => setTeacherFormData({...teacherFormData, teacherPhone: e.target.value})}
                sx={{ mb: 2 }}
                placeholder="010-1234-5678"
                required
              />
              
              <TextField
                fullWidth
                label="이메일"
                type="email"
                value={teacherFormData.teacherEmail}
                onChange={(e) => setTeacherFormData({...teacherFormData, teacherEmail: e.target.value})}
                sx={{ mb: 2 }}
                required
              />
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                할당된 키는 교사가 개인 신청 시 사용할 수 있습니다.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleAssignKey}
            disabled={!teacherFormData.teacherName || !/^[A-Z][A-Z .'-]*$/i.test(teacherFormData.teacherNameEn.trim()) || !teacherFormData.teacherPhone || !teacherFormData.teacherEmail}
          >
            할당
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BTKeyManagePage;
